import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { axiosErrorHandler } from 'common/helpers/axios.helper';
import { AppDispatch, RootState } from 'app/store';
import {
  IGood,
  ICreateGoodPayload,
  IGoodListPayload,
  IGoodParams,
  IRequestForGoodsApprovalPayload,
  IUpdateGoodPayload,
  IManuallyAddedGoodListPayload,
} from 'entities/Goods/Goods.models';
import { goodsTransport } from 'entities/Goods/Goods.transport';
import {
  IContractGoodsSettingsItem,
  IContractGoodsSettingsParams,
  IUnsetContractGoodsSettingsPayload,
  IUpdateContractGoodsAvailabilityPayload,
} from 'entities/Contract/Contract.models';
import { contractTransport } from 'entities/Contract/Contract.transport';
import { IBasketGoods } from 'entities/Basket/Basket.models';

interface IState {
  good: IGood | null;
  goodList: IGood[];
  goodCount: number;
  goodHasMore: boolean;
  isGoodLoading: boolean;
  goodSettings: IContractGoodsSettingsItem[];
}

const initialState: IState = {
  good: null,
  goodList: [],
  goodCount: 0,
  goodHasMore: false,
  isGoodLoading: false,
  goodSettings: [],
};

export const goodSlice = createSlice({
  name: 'good',
  initialState,
  reducers: {
    setGood(state, action: PayloadAction<IGood | null>) {
      state.good = action.payload;
    },
    setGoodList(state, action: PayloadAction<IGood[]>) {
      state.goodList = action.payload;
    },
    setGoodListChunk(state, action: PayloadAction<IGood[]>) {
      state.goodList.push(...action.payload);
    },
    setGoodCount(state, action: PayloadAction<number>) {
      state.goodCount = action.payload;
    },
    setGoodHasMore(state, action: PayloadAction<boolean>) {
      state.goodHasMore = action.payload;
    },
    setIsGoodLoading(state, action: PayloadAction<boolean>) {
      state.isGoodLoading = action.payload;
    },
    setGoodSettings(state, action: PayloadAction<IContractGoodsSettingsItem[]>) {
      state.goodSettings = action.payload;
    },
    addGoodListItem(state, action: PayloadAction<IGood>) {
      state.goodList.push(action.payload);
    },
    patchGoodListItem(state, action: PayloadAction<IGood>) {
      const good = state.goodList.find((item) => item.id === action.payload.id);

      if (!good) {
        return;
      }

      Object.assign(good, action.payload);
    },
    patchGoodListItemGoodInBasket(state, action: PayloadAction<IBasketGoods[]>) {
      const basketGoodIds = new Set(action.payload.map((item) => item.goodId));

      state.goodList.forEach((item) => {
        if (basketGoodIds.has(item.id)) {
          item.goodInBasket = true;
        }
      });
    },
    patchGoodSettingsItem(state, action: PayloadAction<IContractGoodsSettingsItem>) {
      const item = state.goodSettings.find((i) => i.contractId === action.payload.contractId);

      if (!item) {
        return;
      }

      Object.assign(item, action.payload);
    },
    patchGoodSettingsItemIsProgress: (state, action: PayloadAction<{ contractId: number; inProgress: boolean }>) => {
      const setting = state.goodSettings.find((item) => item.contractId === action.payload.contractId);

      if (setting) {
        setting.inProgress = action.payload.inProgress;
      }
    },
  },
});

export const {
  setGood,
  setGoodList,
  setGoodListChunk,
  setGoodCount,
  setGoodHasMore,
  setIsGoodLoading,
  setGoodSettings,
  addGoodListItem,
  patchGoodListItem,
  patchGoodListItemGoodInBasket,
  patchGoodSettingsItem,
  patchGoodSettingsItemIsProgress,
} = goodSlice.actions;

export const selectGood = (state: RootState) => state.good.good;
export const selectGoodList = (state: RootState) => state.good.goodList;
export const selectGoodCount = (state: RootState) => state.good.goodCount;
export const selectGoodHasMore = (state: RootState) => state.good.goodHasMore;
export const selectIsGoodLoading = (state: RootState) => state.good.isGoodLoading;
export const selectGoodSettings = (state: RootState) => state.good.goodSettings;

export const goodReducer = goodSlice.reducer;

export const getGoodList = (payload: IGoodListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsGoodLoading(true));

  try {
    const response = await goodsTransport.get({ ...payload, skipTotalCount: true });
    dispatch(setGoodList(response.data));
    dispatch(setGoodCount(response.count));
    dispatch(setGoodHasMore(!!response.hasMore));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsGoodLoading(false));
  }
};

export const getGoodListChunk = (payload: IGoodListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsGoodLoading(true));

  try {
    const response = await goodsTransport.get({ ...payload, skipTotalCount: true });
    dispatch(setGoodListChunk(response.data));
    dispatch(setGoodHasMore(!!response.hasMore));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsGoodLoading(false));
  }
};

export const getManuallyAddedGoodList = (payload: IManuallyAddedGoodListPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsGoodLoading(true));

  try {
    const response = await goodsTransport.getManuallyAddedGoodList(payload);
    dispatch(setGoodList(response.data));
    dispatch(setGoodCount(response.count));
    // manually-added отдаёт точный count → hasMore считаем из него (загружено < total).
    dispatch(setGoodHasMore(response.data.length < response.count));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsGoodLoading(false));
  }
};

export const getManuallyAddedGoodListChunk =
  (payload: IManuallyAddedGoodListPayload) => async (dispatch: AppDispatch, getState: () => RootState) => {
    dispatch(setIsGoodLoading(true));

    try {
      const response = await goodsTransport.getManuallyAddedGoodList(payload);
      dispatch(setGoodListChunk(response.data));
      dispatch(setGoodHasMore(getState().good.goodList.length < response.count));
      payload.onSuccess?.();
    } catch (error) {
      axiosErrorHandler(error);
    } finally {
      dispatch(setIsGoodLoading(false));
    }
  };

export const getGoodById = (params: IGoodParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsGoodLoading(true));

  try {
    const response = await goodsTransport.getById(params);
    dispatch(setGood(response));
    params.onSuccess?.();
    return response;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsGoodLoading(false));
  }
};

export const createGood = (payload: ICreateGoodPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsGoodLoading(true));

  try {
    const response = await goodsTransport.create(payload);
    payload.onSuccess?.();
    return response;
  } catch (error) {
    axiosErrorHandler(error);
    return null;
  } finally {
    dispatch(setIsGoodLoading(false));
  }
};

export const updateGood = (payload: IUpdateGoodPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsGoodLoading(true));

  try {
    const response = await goodsTransport.update(payload);
    dispatch(setGood(response));
    dispatch(patchGoodListItem(response));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsGoodLoading(false));
  }
};

export const sendRequestForGoodApproval = (payload: IRequestForGoodsApprovalPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsGoodLoading(true));

  try {
    await goodsTransport.sendRequestForGoodsApproval(payload);
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsGoodLoading(false));
  }
};

export const getGoodSettings = (params: IContractGoodsSettingsParams) => async (dispatch: AppDispatch) => {
  dispatch(setIsGoodLoading(true));

  try {
    const response = await contractTransport.getGoodsSettings(params);
    dispatch(setGoodSettings(response.settings));
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsGoodLoading(false));
  }
};

export const updateGoodsAvailability = (payload: IUpdateContractGoodsAvailabilityPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsGoodLoading(true));

  try {
    await contractTransport.updateGoodsAvailability(payload);
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsGoodLoading(false));
  }
};

export const unsetContractGoodSettings = (payload: IUnsetContractGoodsSettingsPayload) => async (dispatch: AppDispatch) => {
  dispatch(setIsGoodLoading(true));

  try {
    const response = await contractTransport.unsetGoodsSettings(payload);
    dispatch(setGoodSettings(response.settings));
    payload.onSuccess?.();
  } catch (error) {
    axiosErrorHandler(error);
  } finally {
    dispatch(setIsGoodLoading(false));
  }
};
